import api from "@/services/api";
import {
  QueryClient,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { computed, MaybeRefOrGetter, reactive, ref, Ref, toValue } from "vue";
import {
  useDataFiltersStore,
  useDetachedTemporalFilter,
} from "@/store/data_filters";
import { useAuthedActiveOrganizationId } from "@/services/user";

// Keep in sync with backend VisibilityProvider.
export type VisibilityProvider =
  | "perplexity"
  | "anthropic"
  | "openai"
  | "google";

export const VISIBILITY_PROVIDER_LABELS: Record<VisibilityProvider, string> = {
  perplexity: "Perplexity",
  anthropic: "Claude",
  openai: "ChatGPT",
  google: "Gemini",
};

// Engines actually wired in the fan-out (backend _PROVIDER_MAP). Others are
// shown in the UI but not selectable (e.g. Gemini — not yet supported).
export const SUPPORTED_VISIBILITY_PROVIDERS: readonly VisibilityProvider[] = [
  "perplexity",
  "openai",
  "anthropic",
];

export function isSupportedProvider(provider: VisibilityProvider): boolean {
  return SUPPORTED_VISIBILITY_PROVIDERS.includes(provider);
}

export type VisibilityCadence = "weekly" | "biweekly" | "monthly";

// Keep in sync with backend PromptSource: "manual" = user-added, "keyword" =
// auto-generated from the org's search demand.
export type PromptSource = "manual" | "keyword";

export interface VisibilityPrompt {
  pk: number;
  text: string;
  localeCountry: string;
  localeCity: string;
  localeRegion: string;
  businessArea: number | null; // BusinessArea pk, or null (brand-level / all)
  providers: VisibilityProvider[];
  enabled: boolean;
  source: PromptSource;
  createdAt: string;
  modifiedAt: string;
}

export interface VisibilityConfig {
  cadence: VisibilityCadence;
  // Optional per-org monthly token ceiling for AI Visibility (null = no
  // feature-specific cap; the org-wide AI token limit still applies).
  // Manager-settable through the config PATCH endpoint, or by an operator in
  // Django admin. No UI control yet.
  // Cadence has an in-app control on the prompts tab; this one does not yet.
  monthlyTokenBudget: number | null;
  lastFannedOutAt: string | null;
  // Set when a paid run was declined for lack of budget, and cleared by the
  // next successful fan-out — so a non-null lastSkippedAt means runs are still
  // being skipped right now, with no staleness check needed on this side.
  // Before this the customer saw nothing at all: a blocked run only logged and
  // raised a Sentry event, while the consent screen promises the feature uses
  // the org's monthly AI allowance (PR 7990 review M4).
  lastSkippedAt: string | null;
  lastSkippedReason: string;
}

export interface ProviderShare {
  mentioned: number;
  total: number;
  share: number; // 0..1
}

// Defined for users in SHARE_OF_VOICE_HELP and MENTION_RATE_HELP below.
export interface BrandProviderShare {
  mentioned: number;
  total: number;
  mentionRate: number; // 0..1
  shareOfVoice: number; // 0..1
}

export interface BrandShareOfVoice extends BrandProviderShare {
  competitorId: number | null;
  isSelf: boolean;
  providers: Partial<Record<VisibilityProvider, BrandProviderShare>>;
}

export const SHARE_OF_VOICE_HELP =
  "Each brand's share of all mentions of the brands listed here in AI answers. Brands you don't track, or that don't compete in the selected business area, aren't counted. It adds up to 100%, so it can fall while your mention rate rises if competitors gain mentions faster.";
export const MENTION_RATE_HELP =
  "The share of AI answers that mention the brand, by name or by citing its site. One answer can mention several brands, so mention rates don't add up to 100%.";

export type ScoreBand = "great" | "good" | "poor";

export interface ShareOfVoiceResponse {
  windowDays: number;
  earliestRunDate: string | null; // ISO date of the org's first run, or null
  score: number | null; // 0..100 mention rate; null if the brand isn't listed
  scoreBand: ScoreBand | null;
  brands: BrandShareOfVoice[];
}

// The trend has two shapes by mode (see useShareOfVoiceTrendQuery):
// - comparative (no competitor): `brands` = share of voice per brand that
//   period (sums to 1).
// - single-brand (competitor set): `total` + `providers` = that brand's mention
//   rate overall + per engine that period, matching the gauge.
export interface TrendPeriod {
  period: string; // ISO date (start of bucket)
  // 0..1, comparative mode; null for a period with runs but no mentions
  brands?: Record<string, number | null>;
  total?: number; // 0..1, single-brand mode
  providers?: Partial<Record<VisibilityProvider, number>>; // single-brand mode
}

// Where an engine's measured model changed: numbers on either side of `date`
// aren't directly comparable, so the trend marks it.
export interface VisibilityModelChange {
  date: string; // ISO date of the first run on the new model
  provider: VisibilityProvider;
  fromModel: string;
  toModel: string;
}

export interface ShareOfVoiceTrendResponse {
  periods: TrendPeriod[];
  modelChanges: VisibilityModelChange[];
}

export interface CitedSource {
  domain: string;
  // Per-engine citation RATE (0..1): distinct answers from that engine citing
  // this source / total answers from that engine. citationRate is the mean of
  // the per-engine rates (equal weight per engine), so it's not biased by how
  // many samples each engine ran. See cited_sources in ai_visibility/views.py.
  citationRate: number;
  isOwned: boolean;
  providers: Partial<Record<VisibilityProvider, number>>; // per-engine rates 0..1
  // Distinct cited pages under this domain. Rows are grouped by domain, so a
  // domain row can stand for many URLs.
  distinctUrls: number;
}

export interface PromptBrandShare {
  competitorId: number | null;
  isSelf: boolean;
  share: number; // 0..1 of the prompt's answers that mention the brand
}

// The newest answer for a prompt: one sample, not a summary of all of them.
export interface LatestAnswer {
  pk: number;
  provider: VisibilityProvider;
  runDate: string;
  preview: string; // plain text
}

export interface PromptPerformance {
  promptId: number;
  text: string;
  businessAreaId: number | null;
  mentioned: number;
  total: number;
  share: number; // 0..1 own-brand appearance rate
  bestRank: number | null; // best (lowest) rank when the brand appeared
  providers: Partial<Record<VisibilityProvider, ProviderShare>>;
  // Present only when ?competitor= was sent — the focused competitor's rate.
  competitorShare?: number; // 0..1
  competitorBestRank?: number | null;
  topBrands: PromptBrandShare[]; // most-mentioned first, at most 5
  latestAnswer: LatestAnswer | null;
  // Answers with text to read: what the answers modal steps through. `total`
  // also counts measured runs that saved no text.
  answers: number;
  answersByProvider: Partial<Record<VisibilityProvider, number>>;
  /** Counts per verdict, empty when nothing on this prompt was scored.
   * Absence of a measurement, which must not render as neutral.
   *
   * Keyed by the CAMELCASED verdict (`slightlyNegative`), not the
   * `VisibilitySentiment` value: the renderer camelCases dict keys at any
   * depth. Typing it by the scale's own values made the mismatch invisible to
   * vue-tsc and the two mild levels silently never rendered. Read it through
   * `sentimentCount` rather than indexing it directly.
   *
   * OPTIONAL: callers that build a prompt row without scoring it — the CSV
   * export's empty row among them — have no counts to supply, and
   * `sentimentCount` reads an absent object as zero of every level. */
  sentiment?: Partial<Record<string, number>>;
}

export interface AnswerCitation {
  url: string;
  domain: string;
  title: string;
  position: number | null;
  isOwned: boolean;
  competitor: number | null;
}

// Only brands the answer actually mentioned, in rank order.
export interface AnswerMention {
  competitor: number | null;
  isSelf: boolean;
  rank: number | null;
  /** "" on an unscored mention (scoring off, a competitor's mention, the
   * brand not named, or the call failed) — absence of a measurement, not
   * neutral. */
  sentiment: VisibilitySentiment | "";
  /** The classifier's own certainty; null only on an unscored mention.
   * Near a coin-flip it renders the same "uncertain" marker as the sentiment
   * table. */
  sentimentConfidence: number | null;
  sentimentReason: string;
}

export interface VisibilityAnswer {
  pk: number;
  prompt: number;
  provider: VisibilityProvider;
  runDate: string;
  sampleIndex: number;
  // The text itself is VisibilityAnswerDetail's.
  citations: AnswerCitation[];
  mentions: AnswerMention[];
}

// The names + aliases the matcher looked for, per brand the answer mentioned.
export interface AnswerBrandTerms {
  competitor: number | null;
  isSelf: boolean;
  terms: string[];
}

export interface VisibilityAnswerDetail extends VisibilityAnswer {
  answerText: string; // markdown
  brandTerms: AnswerBrandTerms[];
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// "self" or a competitor pk narrows to answers that mention that brand.
export type AnswerBrandFilter = "self" | number | null;

export interface CostEstimate {
  cadence: VisibilityCadence;
  runsPerMonth: number;
  monthlyTokens: number;
  monthlyUsd: number;
  monthlyTokenLimit: number;
  // Credit-denominated twins of monthlyTokens and monthlyTokenLimit, and the
  // currency the org allowance is enforced in. Tokens structurally understate
  // this feature, because the grounded web-search fee is a per-search dollar
  // charge and no token count can express it. Serialized as a decimal string.
  monthlyCredits: string;
  // null for an unlimited org — render no percentage rather than dividing.
  monthlyCreditAllowance: number | null;
  // This feature's own ceiling, in TOKENS although the org allowance is in
  // credits. null = no feature-specific cap. It is the cap the fan-out
  // actually stops on, so a paste can sit well inside the org allowance and
  // still be refused here.
  monthlyTokenBudget: number | null;
}

// --- Prompts (CRUD) ---

/**
 * Prompt writes change what a fan-out costs, so the projection has to be
 * refetched with the list. Without this the cost banner keeps showing the
 * previous setup's number until its staleTime lapses — misleading on a bulk
 * paste, which can multiply the figure.
 */
function invalidatePromptsAndCost(queryClient: QueryClient): () => void {
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["visibility-prompts"] });
    void queryClient.invalidateQueries({
      queryKey: ["visibility-cost-estimate"],
    });
  };
}

export function listPrompts(): Promise<VisibilityPrompt[]> {
  return api.get("/ai-visibility/prompts/").then((r) => r.data);
}

export function useVisibilityPromptsQuery() {
  return reactive(
    useQuery({ queryKey: ["visibility-prompts"], queryFn: listPrompts }),
  );
}

export interface PromptPayload {
  text: string;
  localeCountry: string;
  localeCity?: string;
  localeRegion?: string;
  businessArea?: number | null;
  providers: VisibilityProvider[];
  enabled?: boolean;
}

export type BulkPromptRejectionReason =
  | "already_exists"
  | "duplicate_in_batch"
  | "too_long"
  | "empty_after_normalization";

export interface BulkPromptRejection {
  line: number;
  text: string;
  reason: BulkPromptRejectionReason;
  message: string;
  duplicateOfLine?: number;
}

export interface BulkPromptResult {
  created: number;
  skipped: number;
  promptsAfter: number;
  rejected: BulkPromptRejection[];
  costEstimate: CostEstimate;
}

export interface BulkPromptPayload extends Omit<PromptPayload, "text"> {
  /** The raw block; the server splits it so reported line numbers match the textarea. */
  text: string;
  dryRun?: boolean;
}

// The organization travels as a query parameter, not just in the body: the
// server resolves scope from the session's selection, which can move to another
// organization while this page still shows the first. Naming it lets the
// backend refuse with a 409 rather than quote one org and write to another.
function bulkCreatePrompts(
  payload: BulkPromptPayload,
  organizationId: number | undefined,
): Promise<BulkPromptResult> {
  return api
    .post("/ai-visibility/prompts/bulk-create/", payload, {
      params: { organization: organizationId },
    })
    .then((r) => r.data);
}

/**
 * Preview only — the server runs the whole batch and rolls back, so the quoted
 * cost comes from the same parse, dedup and cap check a commit would use. Not a
 * mutation: it has no side effects and must not invalidate anything.
 */
export function previewBulkPrompts(
  payload: Omit<BulkPromptPayload, "dryRun">,
  organizationId: number | undefined,
): Promise<BulkPromptResult> {
  return bulkCreatePrompts({ ...payload, dryRun: true }, organizationId);
}

export function useBulkCreateVisibilityPromptsMutation() {
  const queryClient = useQueryClient();
  // Read at setup, sent per request: the preview and the commit must name the
  // same organization the cost banner beside them was quoted for.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useMutation({
      mutationFn: (payload: Omit<BulkPromptPayload, "dryRun">) =>
        bulkCreatePrompts({ ...payload, dryRun: false }, organizationId.value),
      onSuccess: invalidatePromptsAndCost(queryClient),
    }),
  );
}

export interface GeneratePromptsResult {
  created: number;
  requested: number;
  prompts: VisibilityPrompt[];
  promptsAfter: number;
  costEstimate: CostEstimate;
}

/** Why a generation produced nothing. The server distinguishes these because
 * "no search data" and "the provider failed" need different responses from the
 * user — retrying helps with one and not the other. */
export type GenerationRefusalReason =
  | "flag_disabled"
  | "spend_blocked"
  | "at_capacity"
  | "no_user"
  | "no_signals"
  | "generation_failed";

export interface GenerationReadiness {
  signalsAvailable: number;
  signalsNeeded: number;
  sufficient: boolean;
  hasSignals: boolean;
}

export const generationReadinessKeys = {
  all: ["visibility-generation-readiness"] as const,
  readiness: (params: { organizationId?: number; count: number }) =>
    [...generationReadinessKeys.all, params] as const,
};

/**
 * Whether auto-generation has enough search data behind it. Advisory: a false
 * `sufficient` warns, it never blocks — an org with thin data can still
 * generate, and can always write prompts by hand.
 */
export function useGenerationReadinessQuery(
  count: Ref<number>,
  enabled?: Ref<boolean>,
) {
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(() =>
        generationReadinessKeys.readiness({
          organizationId: organizationId.value,
          count: count.value,
        }),
      ),
      queryFn: ({ queryKey }) =>
        api
          .get("/ai-visibility/prompts/generation-readiness/", {
            params: {
              organization: queryKey[1].organizationId,
              count: queryKey[1].count,
            },
          })
          .then((r) => r.data as GenerationReadiness),
      enabled: computed(
        () => organizationId.value !== undefined && (enabled?.value ?? true),
      ),
    }),
  );
}

export function useGenerateVisibilityPromptsMutation() {
  const queryClient = useQueryClient();
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useMutation({
      mutationFn: (count: number): Promise<GeneratePromptsResult> =>
        api
          .post(
            "/ai-visibility/prompts/generate/",
            { count },
            { params: { organization: organizationId.value } },
          )
          .then((r) => r.data),
      onSuccess: () => {
        invalidatePromptsAndCost(queryClient)();
        // The readiness figure counts against stored prompts, so it moves once
        // a generation lands.
        void queryClient.invalidateQueries({
          queryKey: generationReadinessKeys.all,
        });
      },
    }),
  );
}

export function useUpdateVisibilityPromptMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: ({
        pk,
        payload,
      }: {
        pk: number;
        payload: Partial<PromptPayload>;
      }): Promise<VisibilityPrompt> =>
        api.patch(`/ai-visibility/prompts/${pk}/`, payload).then((r) => r.data),
      onSuccess: invalidatePromptsAndCost(queryClient),
    }),
  );
}

export function useDeleteVisibilityPromptMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: (pk: number): Promise<void> =>
        api.delete(`/ai-visibility/prompts/${pk}/`).then(() => undefined),
      onSuccess: invalidatePromptsAndCost(queryClient),
    }),
  );
}

// --- Aggregates (read) ---

// area: null = org-wide (all areas); a pk scopes to that business area.
function areaParam(area: number | null): { business_area?: number } {
  return area !== null ? { business_area: area } : {};
}

export interface Scope {
  from: string;
  to: string;
  provider?: string; // engine lens; omitted = all engines
}

// The shared query scope for the AI-visibility view: the date window (from the
// global temporal filter, defaulted by CompetitorMonitorBase to the org's full
// data range, narrowable) plus the engine lens (aiVisibilityProvider store key,
// "" = all). Sent to the backend as from/to[/provider].
export function useScope(): Ref<Scope> {
  const { selection_from, selection_to } = useDetachedTemporalFilter();
  const store = useDataFiltersStore();
  return computed(() => ({
    from: selection_from.value,
    to: selection_to.value,
    ...(store.aiVisibilityProvider
      ? { provider: store.aiVisibilityProvider }
      : {}),
  }));
}

// competitor: null = the org's own brand; a pk targets that competitor.
export function getShareOfVoice(
  scope: Scope,
  competitor: number | null,
  area: number | null,
  organizationId: number | undefined,
  signal: AbortSignal,
): Promise<ShareOfVoiceResponse> {
  return api
    .get("/ai-visibility/share_of_voice/", {
      signal,
      params: {
        organization: organizationId,
        ...scope,
        ...(competitor !== null && { competitor }),
        ...areaParam(area),
      },
    })
    .then((r) => r.data);
}

export function useShareOfVoiceQuery(
  competitor: Ref<number | null>,
  area: Ref<number | null> = ref(null),
) {
  const scope = useScope();
  // Organization-scoped like costEstimateKeys: keyed on it, so a switch never
  // renders the previous one's data, and sent with the request, so a reply
  // for another organization (the session moved mid-flight) is refused.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-share-of-voice",
        { organizationId },
        scope,
        competitor,
        area,
      ] as const,
      // From the KEY, not the live refs — see useCostEstimateQuery.
      queryFn: ({ queryKey, signal }) =>
        getShareOfVoice(
          queryKey[2],
          queryKey[3],
          queryKey[4],
          queryKey[1].organizationId,
          signal,
        ),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

// brand: null = comparative (one line per brand); "self"/pk = single-brand
// (one line per engine for that brand).
function getShareOfVoiceTrend(
  scope: Scope,
  period: "month" | "week",
  area: number | null,
  brand: string | number | null,
  organizationId: number | undefined,
  signal: AbortSignal,
): Promise<ShareOfVoiceTrendResponse> {
  return api
    .get("/ai-visibility/share_of_voice_trend/", {
      signal,
      params: {
        organization: organizationId,
        ...scope,
        period,
        ...areaParam(area),
        ...(brand !== null && { competitor: brand }),
      },
    })
    .then((r) => r.data);
}

export function useShareOfVoiceTrendQuery(
  area: Ref<number | null> = ref(null),
  brand: Ref<string | number | null> = ref(null),
  period: "month" | "week" = "month",
) {
  const scope = useScope();
  // Organization-scoped like costEstimateKeys: keyed on it, so a switch never
  // renders the previous one's data, and sent with the request, so a reply
  // for another organization (the session moved mid-flight) is refused.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-share-of-voice-trend",
        { organizationId },
        scope,
        period,
        area,
        brand,
      ] as const,
      // From the KEY, not the live refs — see useCostEstimateQuery.
      queryFn: ({ queryKey, signal }) =>
        getShareOfVoiceTrend(
          queryKey[2],
          queryKey[3],
          queryKey[4],
          queryKey[5],
          queryKey[1].organizationId,
          signal,
        ),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

// brand: undefined/null = all cited sources; "self" = owned; a pk = that
// competitor's attributed citations. area: null = all areas.
// all: every cited domain instead of the top 100 — for exports, which must
// be complete.
export function getCitedSources(
  scope: Scope,
  brand: string | number | null,
  area: number | null,
  organizationId: number | undefined,
  signal: AbortSignal,
  all = false,
): Promise<{
  sources: CitedSource[];
  // Distinct cited pages across EVERY domain in scope. `sources` is truncated
  // to the top 100 domains, so summing its rows undercounts an organization
  // with more cited domains than that — read this for a total.
  totalDistinctUrls: number;
  // Engines that ran in scope, cited or not — `sources` only names engines
  // that cited something.
  enginesRun: VisibilityProvider[];
}> {
  return api
    .get("/ai-visibility/cited_sources/", {
      signal,
      params: {
        organization: organizationId,
        ...scope,
        ...(brand !== null && { brand }),
        ...areaParam(area),
        ...(all && { all: true }),
      },
    })
    .then((r) => r.data);
}

export function useCitedSourcesQuery(
  brand: Ref<string | number | null> = ref(null),
  area: Ref<number | null> = ref(null),
) {
  const scope = useScope();
  // Organization-scoped like costEstimateKeys: keyed on it, so a switch never
  // renders the previous one's data, and sent with the request, so a reply
  // for another organization (the session moved mid-flight) is refused.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-cited-sources",
        { organizationId },
        scope,
        brand,
        area,
      ] as const,
      // From the KEY, not the live refs — see useCostEstimateQuery.
      queryFn: ({ queryKey, signal }) =>
        getCitedSources(
          queryKey[2],
          queryKey[3],
          queryKey[4],
          queryKey[1].organizationId,
          signal,
        ),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

export function getPromptPerformance(
  scope: Scope,
  area: number | null,
  competitor: number | null,
  organizationId: number | undefined,
  signal: AbortSignal,
): Promise<{ prompts: PromptPerformance[] }> {
  return api
    .get("/ai-visibility/prompt_performance/", {
      signal,
      params: {
        organization: organizationId,
        ...scope,
        ...areaParam(area),
        ...(competitor !== null && { competitor }),
      },
    })
    .then((r) => r.data);
}

// competitor: a pk adds that competitor's per-prompt column alongside self.
export function usePromptPerformanceQuery(
  area: Ref<number | null> = ref(null),
  competitor: Ref<number | null> = ref(null),
) {
  const scope = useScope();
  // Organization-scoped like costEstimateKeys: keyed on it, so a switch never
  // renders the previous one's data, and sent with the request, so a reply
  // for another organization (the session moved mid-flight) is refused.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-prompt-performance",
        { organizationId },
        scope,
        area,
        competitor,
      ] as const,
      // From the KEY, not the live refs — see useCostEstimateQuery.
      queryFn: ({ queryKey, signal }) =>
        getPromptPerformance(
          queryKey[2],
          queryKey[3],
          queryKey[4],
          queryKey[1].organizationId,
          signal,
        ),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

export const costEstimateKeys = {
  all: ["visibility-cost-estimate"] as const,
  estimate: (params: { organizationId?: number } = {}) =>
    [...costEstimateKeys.all, params] as const,
};

export function useCostEstimateQuery() {
  // Organization-scoped: the payload carries this org's token limit and
  // credit allowance, and switching organizations invalidates without
  // evicting — so on a flat key the newly selected org renders the previous
  // one's allowance until the refetch lands. The request names the
  // organization too, because scope is resolved from the session's
  // server-side selection, which can move while the request is in flight.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(() =>
        costEstimateKeys.estimate({ organizationId: organizationId.value }),
      ),
      // From the KEY, not the live ref — see useAITokenUsageQuery.
      queryFn: ({ queryKey, signal }): Promise<CostEstimate> =>
        api
          .get("/ai-visibility/cost_estimate/", {
            params: { organization: queryKey[1].organizationId },
            signal,
          })
          .then((r) => r.data),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

export const visibilityConfigKeys = {
  all: ["visibility-config"] as const,
  config: (params: { organizationId?: number } = {}) =>
    [...visibilityConfigKeys.all, params] as const,
};

/**
 * Organization-scoped, for the same reason as the cost estimate: switching
 * organizations invalidates without evicting, so on a flat key the newly
 * selected organization renders the previous one's cadence — and its
 * "scheduled runs are paused" notice — until a refetch lands.
 */
export function useVisibilityConfigQuery() {
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(() =>
        visibilityConfigKeys.config({ organizationId: organizationId.value }),
      ),
      queryFn: ({ queryKey }): Promise<VisibilityConfig> =>
        api
          .get("/ai-visibility/config/", {
            // The server skips the scope check when this is absent, so without
            // it the org-keyed cache can still be filled with another org's
            // cadence.
            params: { organization: queryKey[1].organizationId },
          })
          .then((r) => r.data),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

/**
 * Cadence is the biggest single cost lever — `runs_per_month` derives straight
 * from it, so weekly against monthly is roughly a 4x swing on the bill. The
 * cost estimate is therefore invalidated alongside the config: leaving it to
 * its own stale timer would show the new cadence beside the old quote.
 *
 * PATCH is manager-only (the GET is not), so a member calling this gets a 403.
 */
export function useUpdateVisibilityConfigMutation() {
  const queryClient = useQueryClient();
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useMutation({
      mutationFn: (
        // Writable fields only: lastFannedOutAt / lastSkippedAt /
        // lastSkippedReason are read_only on VisibilityConfigSerializer, so a
        // bare Partial<VisibilityConfig> would let a caller pass them and
        // watch the write silently do nothing.
        payload: Partial<
          Pick<VisibilityConfig, "cadence" | "monthlyTokenBudget">
        >,
      ): Promise<{
        config: VisibilityConfig;
        organizationId: number | undefined;
      }> => {
        // Captured at CLICK time and carried through: the session can move
        // between the request and its response, and seeding the cache under
        // the org that is current THEN would file this org's cadence under
        // another one's key.
        const requestedOrganizationId = organizationId.value;
        return api
          .patch("/ai-visibility/config/", payload, {
            params: { organization: requestedOrganizationId },
          })
          .then((r) => ({
            config: r.data as VisibilityConfig,
            organizationId: requestedOrganizationId,
          }));
      },
      onSuccess: ({ config, organizationId: requestedOrganizationId }) => {
        // Seed the cache from the response rather than only invalidating: an
        // invalidated query keeps serving its previous data while it refetches,
        // so the control would show the OLD cadence until the estimate came
        // back — and keep showing it if that refetch failed.
        queryClient.setQueryData(
          visibilityConfigKeys.config({
            organizationId: requestedOrganizationId,
          }),
          config,
        );
        void queryClient.invalidateQueries({
          queryKey: visibilityConfigKeys.all,
        });
        void queryClient.invalidateQueries({ queryKey: costEstimateKeys.all });
      },
    }),
  );
}

// Organization-scoped for the same reason as costEstimateKeys: switching
// organizations invalidates without evicting, so a flat key would show the
// previous organization's answers until the refetch lands.
export const visibilityAnswerKeys = {
  all: ["visibility-answers"] as const,
  list: (params: {
    organizationId?: number;
    prompt: number | null;
    page: number;
    brand: AnswerBrandFilter;
    scope: Scope;
    pageSize?: number;
    runDate?: string;
    // One exact run. (provider, runDate) is not unique — samples of the same
    // prompt on the same day differ by sample_index.
    run?: number;
  }) => [...visibilityAnswerKeys.all, "list", params] as const,
  runDates: (params: {
    organizationId?: number;
    prompt: number | null;
    brand: AnswerBrandFilter;
    scope: Scope;
  }) => [...visibilityAnswerKeys.all, "run-dates", params] as const,
  detail: (params: { organizationId?: number; pk: number | null }) =>
    [...visibilityAnswerKeys.all, "detail", params] as const,
};

type AnswerListParams = Parameters<typeof visibilityAnswerKeys.list>[0];

export function samePagedList(
  a: AnswerListParams,
  b: AnswerListParams,
): boolean {
  const { page: _a, ...restA } = a;
  const { page: _b, ...restB } = b;
  return JSON.stringify(restA) === JSON.stringify(restB);
}

export function useVisibilityAnswersQuery(
  prompt: Ref<number | null>,
  page: Ref<number>,
  brand: Ref<AnswerBrandFilter> = ref(null),
  options: {
    // Narrows to one engine, overriding the page's engine lens.
    provider?: Ref<VisibilityProvider | null>;
    pageSize?: number;
    runDate?: Ref<string | null>; // ISO date of one run
    run?: Ref<number | null>; // one exact run, by id
    enabled?: Ref<boolean>;
  } = {},
) {
  const organizationId = useAuthedActiveOrganizationId();
  const pageScope = useScope();
  function queryParams(): AnswerListParams {
    const provider = options.provider?.value;
    return {
      organizationId: organizationId.value,
      prompt: prompt.value,
      page: page.value,
      brand: brand.value,
      scope: provider ? { ...pageScope.value, provider } : pageScope.value,
      ...(options.pageSize !== undefined && { pageSize: options.pageSize }),
      ...(options.runDate?.value && { runDate: options.runDate.value }),
      ...(options.run?.value !== null &&
        options.run?.value !== undefined && { run: options.run.value }),
    };
  }
  return reactive(
    useQuery({
      queryKey: computed(() => visibilityAnswerKeys.list(queryParams())),
      // From the KEY, not the live refs — see useCostEstimateQuery.
      queryFn: ({ queryKey, signal }): Promise<Paginated<VisibilityAnswer>> => {
        const params = queryKey[2];
        return api
          .get("/ai-visibility/answers/", {
            params: {
              organization: params.organizationId,
              prompt: params.prompt,
              page: params.page,
              ...(params.pageSize !== undefined && {
                page_size: params.pageSize,
              }),
              ...(params.runDate && { run_date: params.runDate }),
              ...(params.run !== undefined && { run: params.run }),
              ...params.scope,
              ...(params.brand !== null && { brand: params.brand }),
            },
            signal,
          })
          .then((r) => r.data);
      },
      enabled: computed(
        () =>
          organizationId.value !== undefined &&
          prompt.value !== null &&
          (options.enabled?.value ?? true),
      ),
      // Keep the current page on screen while the next one loads — but only
      // across a PAGE change. Carrying data across any other key change would
      // show another prompt's (or another organization's) answers.
      placeholderData: (previous, previousQuery) =>
        previousQuery && samePagedList(previousQuery.queryKey[2], queryParams())
          ? previous
          : undefined,
    }),
  );
}

export interface AnswerRunDate {
  runDate: string;
  count: number;
}

// The run dates a prompt's answers fall on, under the same filters as the
// answers list, for narrowing it to one run.
export function useVisibilityAnswerRunDatesQuery(
  prompt: Ref<number | null>,
  brand: Ref<AnswerBrandFilter>,
  provider: Ref<VisibilityProvider | null>,
  enabled: Ref<boolean> = ref(true),
) {
  const organizationId = useAuthedActiveOrganizationId();
  const pageScope = useScope();
  const queryKey = computed(() =>
    visibilityAnswerKeys.runDates({
      organizationId: organizationId.value,
      prompt: prompt.value,
      brand: brand.value,
      scope: provider.value
        ? { ...pageScope.value, provider: provider.value }
        : pageScope.value,
    }),
  );
  // What the dates MEAN, as opposed to how they are filtered. `organization`,
  // `prompt` and the date window change which dates exist, so a held response
  // describes a different question once any of them moves. `provider` and
  // `brand` only filter — the server ignores both for the per-engine counts,
  // so a held response across an engine switch is the same answer, and that
  // is what stops the tabs collapsing mid-switch. A consumer guarding stale
  // data wants this, not the whole key.
  const dataIdentity = computed(() => {
    const { from, to } = pageScope.value;
    return JSON.stringify({
      organizationId: organizationId.value,
      prompt: prompt.value,
      from,
      to,
    });
  });
  const query = reactive(
    useQuery({
      queryKey,
      queryFn: ({
        queryKey,
        signal,
      }): Promise<{
        runDates: AnswerRunDate[];
        answersByProvider: Partial<Record<VisibilityProvider, number>>;
      }> => {
        const params = queryKey[2];
        return api
          .get("/ai-visibility/answers/run_dates/", {
            params: {
              organization: params.organizationId,
              prompt: params.prompt,
              ...params.scope,
              ...(params.brand !== null && { brand: params.brand }),
            },
            signal,
          })
          .then((r) => r.data);
      },
      // The key varies by provider and brand, so switching engine refetches
      // and `data` is briefly undefined — which collapsed the modal's engine
      // tabs to a lone "All engines", taking the tab just clicked with them.
      // The server ignores both filters for `answers_by_provider`, so the
      // previous response's counts are not merely close, they are the same
      // answer. The date list does vary, and holding the old one for the
      // length of one fetch is what the date select already did.
      placeholderData: keepPreviousData,
      enabled: computed(
        () =>
          organizationId.value !== undefined &&
          prompt.value !== null &&
          enabled.value,
      ),
    }),
  );
  return { query, dataIdentity };
}

export function useVisibilityAnswerDetailQuery(
  pk: Ref<number | null>,
  enabled: Ref<boolean> = ref(true),
) {
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(() =>
        visibilityAnswerKeys.detail({
          organizationId: organizationId.value,
          pk: pk.value,
        }),
      ),
      queryFn: ({ queryKey, signal }): Promise<VisibilityAnswerDetail> => {
        const params = queryKey[2];
        return api
          .get(`/ai-visibility/answers/${params.pk}/`, {
            params: { organization: params.organizationId },
            signal,
          })
          .then((r) => r.data);
      },
      enabled: computed(
        () =>
          organizationId.value !== undefined &&
          pk.value !== null &&
          enabled.value,
      ),
    }),
  );
}

export type VisibilitySentiment =
  | "negative"
  | "slightly_negative"
  | "neutral"
  | "slightly_positive"
  | "positive";

export interface SentimentExample {
  sentiment: VisibilitySentiment;
  /** Always "" for Decisions verdicts, which return a probability rather
   * than prose. */
  reason: string;
  /** The answer's own words around the brand's first mention — the primary
   * evidence, bounded to ~360 chars. */
  snippet: string;
  /** 0..1 from the classifier. */
  confidence: number | null;
  prompt: string;
  provider: VisibilityProvider;
  runDate: string;
}

export interface SentimentSummary {
  /** The org's own names, for highlighting the brand inside a snippet. */
  brandTerms: string[];
  /** Scored mentions — the denominator. NOT the number of mentions: unscored
   * mentions are excluded rather than counted as neutral. */
  scored: number;
  negative: number;
  slightlyNegative: number;
  neutral: number;
  slightlyPositive: number;
  positive: number;
  /** Null when nothing is scored — "0% negative" would claim a measurement
   * that was never taken. */
  negativeRate: number | null;
  positiveRate: number | null;
  windowDays: number;
  examples: SentimentExample[];
}

function getSentiment(
  scope: Scope,
  area: number | null,
  organizationId: number | undefined,
  signal: AbortSignal,
): Promise<SentimentSummary> {
  return api
    .get("/ai-visibility/sentiment/", {
      signal,
      params: {
        organization: organizationId,
        ...scope,
        ...areaParam(area),
      },
    })
    .then((r) => r.data);
}

export function useSentimentQuery(area: Ref<number | null> = ref(null)) {
  const scope = useScope();
  // Organization-scoped like the other aggregates: keyed on it so a switch
  // never renders the previous org's data, and sent with the request so a
  // reply for an org the session has left is refused.
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-sentiment",
        { organizationId },
        scope,
        area,
      ] as const,
      queryFn: ({ queryKey, signal }) =>
        getSentiment(
          queryKey[2],
          queryKey[3],
          queryKey[1].organizationId,
          signal,
        ),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

export type SuggestedCompetitorKind =
  | "competitor"
  | "comparison_or_directory"
  | "publisher_or_press"
  | "regulator_or_public_body"
  | "other";

export type SuggestedCompetitorStatus = "pending" | "accepted" | "dismissed";

export interface SuggestedCompetitor {
  pk: number;
  domain: string;
  name: string;
  kind: SuggestedCompetitorKind;
  reason: string;
  status: SuggestedCompetitorStatus;
  citationRuns: number;
  samplePrompts: string[];
  // Whether accepting would CREATE a competitor rather than reuse one, decided
  // server-side by the same rule the accept path applies — the plan cap guards
  // a create, and the client must not keep its own copy of that matching.
  wouldCreate: boolean;
  firstSeenAt: string;
}

export const suggestedCompetitorKeys = {
  all: ["visibility-suggested-competitors"] as const,
  list: (params: { organizationId?: number } = {}) =>
    [...suggestedCompetitorKeys.all, params] as const,
};

/**
 * Competitor suggestions awaiting a decision.
 *
 * Organization-scoped for the same reason as the cost estimate: switching
 * organizations invalidates without evicting, so on a flat key the newly
 * selected organization would render the previous one's suggestions until a
 * refetch lands — and these carry an Accept button that writes.
 */
export function useSuggestedCompetitorsQuery(options?: {
  enabled?: MaybeRefOrGetter<boolean>;
}) {
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: computed(() =>
        suggestedCompetitorKeys.list({ organizationId: organizationId.value }),
      ),
      queryFn: ({ queryKey }): Promise<SuggestedCompetitor[]> =>
        api
          .get("/ai-visibility/suggested-competitors/", {
            params: { organization: queryKey[1].organizationId },
          })
          .then((r) => r.data),
      // The endpoint enforces IsAiVisibilityEnabled, and the default retry
      // skips only 404 and 429 — so without a gate an org without the feature
      // fires four failing requests per visit to the competitors page.
      enabled: computed(
        () =>
          organizationId.value !== undefined &&
          toValue(options?.enabled ?? true),
      ),
    }),
  );
}

export interface SentimentTrendPeriod {
  period: string;
  /** Scored mentions in this bucket — the denominator for its rate. Zero
   * where the period was measured but nothing in it was scored; the rates
   * are null there rather than 0, which would read as a measured result. */
  scored: number;
  negativeRate: number | null;
  neutralRate: number | null;
  positiveRate: number | null;
  /** Per-provider negative rate. Samples are tiered, so the engines behind
   * these rates have very different sample sizes. Null where that engine
   * scored nothing in the bucket, which is not the same as a rate of 0 —
   * the serializer sends `FloatField(allow_null=True)` per provider. */
  providers: Record<string, number | null>;
}

export interface SentimentTrend {
  periods: SentimentTrendPeriod[];
}

function getSentimentTrend(
  scope: Scope,
  area: number | null,
  organizationId: number | undefined,
  signal: AbortSignal,
): Promise<SentimentTrend> {
  return api
    .get("/ai-visibility/sentiment_trend/", {
      signal,
      params: {
        organization: organizationId,
        ...scope,
        ...areaParam(area),
      },
    })
    .then((r) => r.data);
}

export function useSentimentTrendQuery(area: Ref<number | null> = ref(null)) {
  const scope = useScope();
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-sentiment-trend",
        { organizationId },
        scope,
        area,
      ] as const,
      queryFn: ({ queryKey, signal }) =>
        getSentimentTrend(
          queryKey[2],
          queryKey[3],
          queryKey[1].organizationId,
          signal,
        ),
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

export interface ScoredAnswer {
  runId: number;
  promptId: number;
  prompt: string;
  provider: VisibilityProvider;
  runDate: string;
  sentiment: VisibilitySentiment;
  sentimentReason: string;
  snippet: string;
  confidence: number | null;
}

export interface ScoredAnswerPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: ScoredAnswer[];
}

function getScoredAnswers(
  scope: Scope,
  area: number | null,
  verdict: VisibilitySentiment | null,
  page: number,
  organizationId: number | undefined,
  signal: AbortSignal,
): Promise<ScoredAnswerPage> {
  return api
    .get("/ai-visibility/scored_answers/", {
      signal,
      params: {
        organization: organizationId,
        page,
        ...scope,
        ...areaParam(area),
        ...(verdict ? { sentiment: verdict } : {}),
      },
    })
    .then((r) => r.data);
}

export function useScoredAnswersQuery(
  verdict: Ref<VisibilitySentiment | null>,
  page: Ref<number>,
  area: Ref<number | null> = ref(null),
) {
  const scope = useScope();
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useQuery({
      queryKey: [
        "visibility-scored-answers",
        { organizationId },
        scope,
        area,
        verdict,
        page,
      ] as const,
      queryFn: ({ queryKey, signal }) =>
        getScoredAnswers(
          queryKey[2],
          queryKey[3],
          queryKey[4],
          queryKey[5],
          queryKey[1].organizationId,
          signal,
        ),
      // Each page is its own cache key, so without this the rows and the count
      // vanish mid-flight on every page step: the table unmounts, the card
      // collapses to its min height, and the Previous/Next buttons move out
      // from under the cursor.
      placeholderData: keepPreviousData,
      enabled: computed(() => organizationId.value !== undefined),
    }),
  );
}

export function useDecideSuggestedCompetitorMutation() {
  const queryClient = useQueryClient();
  const organizationId = useAuthedActiveOrganizationId();
  return reactive(
    useMutation({
      mutationFn: ({
        pk,
        status,
      }: {
        pk: number;
        status: Extract<SuggestedCompetitorStatus, "accepted" | "dismissed">;
      }): Promise<SuggestedCompetitor> =>
        api
          .patch(
            `/ai-visibility/suggested-competitors/${pk}/`,
            { status },
            { params: { organization: organizationId.value } },
          )
          .then((r) => r.data),
      // onSettled, not onSuccess: a refusal means someone else decided this
      // suggestion, so the cached card is stale in exactly the case the user
      // most needs it refreshed. Leaving it actionable lets them retry a
      // decision that can only keep failing.
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: suggestedCompetitorKeys.all,
        });
        // Accepting creates a Competitor, so the competitor list this sits
        // beside is now stale.
        void queryClient.invalidateQueries({ queryKey: ["competitors"] });
      },
    }),
  );
}
